import { Collapse, Icon, IconButton, Typography } from '@material-ui/core';
import { ArrowDropDown, ArrowRight } from '@material-ui/icons';
import copy from 'copy-to-clipboard';
import MaterialTable, { Column } from 'material-table';
import moment from 'moment';
import React from 'react';
import ReactJson, { OnCopyProps } from 'react-json-view';
// @ts-ignore
import XMLViewer from 'react-xml-viewer';

import { IObject } from '~/src/dash/api/resource-transforms';
import { CopyButton } from '~/src/dash/components/button';
import { Col, Cols, Div, Row, Rows, Text } from '~/src/dash/publicaUI';
import { IBaseState } from '~/src/dash/types/props';
import { utils } from '~/src/dash/utils/json';

// This component is a general table to display live logs.

// utils for data
interface IUtils {
  idx: number; // used for the collapse
}

export interface IMandatory {
  [key: string]: any;
  timestamp: string; // used to sort
}

// If you want a detailPanel, you must put an array of INestedData in props.
// Each INestedData is an element in this detai panel
export interface INestedData<TRow> {
  // - TRow used for render()
  // - string used for preformat=text
  // - object used for preformat=json
  getData: (row: TRow) => string | object | any;
  title: string;
  getDataToCopy?: (row: TRow) => string;
  render?: (data: string | object | any) => React.ReactElement;
  preFormat?: 'json' | 'text' | 'xml' | 'json-2'; // if no render func, we use the preformat to know how to display the data
  isNested?: boolean; // default = false
}

export interface IProps<TRow> {
  data: TRow[];
  columns: Array<Column<TRow & object>>;
  nestedData?: Array<INestedData<TRow>>;
  publisherTimezone?: string;
}

interface IState<TRow> extends IBaseState {
  tableData: Array<TRow & IUtils & IMandatory>;
  activeIndex: number;
}

function formatAndSortData<TRow extends object & IMandatory>(
  nestedDataLength: number,
  data: TRow[],
  publisherTimezone: string,
): Array<TRow & IMandatory & IUtils> {
  if (!Array.isArray(data) || data === undefined) {
    return [];
  }

  let i = 0;
  // @ts-ignore
  const mappedData = (data || [])
    .map((d: IObject): TRow | IObject => {
      i = i + nestedDataLength + 1;

      return {
        ...d,
        timestamp:
          moment(d.timestamp).tz(publisherTimezone).format('DD-MM-YYYY[\n]HH:mm:ss') +
          '\n' +
          publisherTimezone,
        idx: i,
      };
    })
    .sort((a: TRow, b: TRow): number => {
      // Sort data by timestamp desc
      return (
        moment(b.timestamp, 'DD-MM-YYYY HH:mm:ss').unix() -
        moment(a.timestamp, 'DD-MM-YYYY HH:mm:ss').unix()
      );
    });

  return mappedData;
}

export class LiveLogsGeneralTable<TRow extends object & IMandatory> extends React.Component<
  IProps<TRow>,
  IState<TRow>
> {
  // properties:
  tableColumns = this.props.columns;

  state = {
    tableData: formatAndSortData<TRow>(
      (this.props.nestedData || []).length,
      this.props.data,
      this.props.publisherTimezone || 'UTC',
    ),
    activeIndex: -1,
  };

  handleCopy = (copyObj: OnCopyProps) => {
    copy(JSON.stringify(copyObj.src, null, '\t'));
  };

  handleClick = (index: number) => {
    const { activeIndex } = this.state;
    const newIndex = activeIndex === index ? -1 : index;

    this.setState({ activeIndex: newIndex });
  };

  componentDidUpdate(prevProps: Readonly<IProps<TRow>>): void {
    if (prevProps.data !== this.props.data) {
      this.setState({
        tableData: formatAndSortData<TRow>(
          (this.props.nestedData || []).length,
          this.props.data,
          'UTC',
        ),
      });
    }
  }

  renderData(nestedData: INestedData<TRow>, data: string | object | any): React.ReactElement {
    if (nestedData.render !== undefined) {
      return nestedData.render(data);
    }

    switch (nestedData.preFormat) {
      case 'json':
        return (
          <ReactJson
            displayDataTypes={false}
            src={utils.json.SafeParse(data as string)}
            enableClipboard={this.handleCopy}
          />
        );
      case 'json-2':
        return (
          <ReactJson
            displayDataTypes={false}
            src={utils.json.SafeParse(JSON.stringify(data))}
            enableClipboard={this.handleCopy}
          />
        );
      case 'xml':
        return <XMLViewer xml={data} invalidXml={<p>{data}</p>} />;
      case 'text':
        return (
          <Typography variant='body2' style={{ whiteSpace: 'pre-line', paddingLeft: '20px' }}>
            {data as string}
          </Typography>
        );
      default:
        console.error('Missing preformat for data, render method is also undefined');
        return <></>;
    }
  }

  createDetailPanel(rowData: TRow & IUtils & IMandatory): React.ReactNode | undefined {
    if (
      this.props === undefined ||
      this.props.nestedData === undefined ||
      this.props.nestedData.length === 0
    ) {
      return undefined;
    }

    return (
      <>
        {this.props.nestedData.map((nestedData: INestedData<TRow>) => {
          const data = nestedData.getData(rowData);

          if (nestedData.isNested) {
            return (
              <Rows>
                <Row noPadding style={{ maxWidth: '800px' }}>
                  <Cols style={{ alignItems: 'center' }}>
                    <Col>
                      <IconButton size='small' onClick={() => this.handleClick(rowData.idx)}>
                        {this.state.activeIndex === rowData.idx ? (
                          <ArrowDropDown />
                        ) : (
                          <ArrowRight />
                        )}
                      </IconButton>
                    </Col>
                    <Col>
                      <Text bold small>
                        {nestedData.title}
                      </Text>
                    </Col>
                    {nestedData.getDataToCopy && (
                      <Col>{this.renderCopyIcon(nestedData.getDataToCopy(rowData))}</Col>
                    )}
                  </Cols>
                </Row>
                <Row noPadding>
                  <Collapse in={this.state.activeIndex === rowData.idx}>
                    <Div style={{ paddingLeft: '2em' }}>{this.renderData(nestedData, data)}</Div>
                  </Collapse>
                </Row>
              </Rows>
            );
          }

          return (
            <Rows
              style={{
                border: '1px solid #f1f1f1',
                borderBottomWidth: '2px',
                borderRadius: '1em',
                display: 'inline-flex',
                padding: '1em 1.5em',
                margin: '0.5em 0',
              }}
            >
              <Row noPadding style={{ maxWidth: '800px' }}>
                <Cols>
                  <Col marginLeft='0.5em'>
                    <Text bold large>
                      {nestedData.title}
                    </Text>
                  </Col>
                  {nestedData.getDataToCopy && (
                    <Col>{this.renderCopyIcon(nestedData.getDataToCopy(rowData))}</Col>
                  )}
                </Cols>
              </Row>
              <Row noPadding>{this.renderData(nestedData, data)}</Row>
            </Rows>
          );
        })}
      </>
    );
  }

  renderCopyIcon(data: string): React.ReactElement {
    return (
      <IconButton onClick={() => copy(data)} size='small'>
        <Icon style={{ fontSize: '0.9em' }} color='disabled'>
          content_copy
        </Icon>
      </IconButton>
    );
  }

  render() {
    return (
      <div>
        <MaterialTable
          detailPanel={
            this.props.nestedData === undefined || this.props.nestedData.length === 0
              ? undefined
              : (rowData: TRow & IUtils & IMandatory) => this.createDetailPanel(rowData)
          }
          style={{
            paddingLeft: '20px',
            paddingRight: '20px',
          }}
          title=''
          options={{
            search: false,
            pageSize: 15,
            pageSizeOptions: [5, 15, 30],
            toolbar: false,
          }}
          columns={this.tableColumns}
          data={this.state.tableData}
        />
      </div>
    );
  }
}
